import React, { useEffect, useMemo, useState } from 'react';
import { listVectorTools } from '../utils/api';
import { mergeSyntheticToolsIntoPickerList } from '../utils/syntheticChatTools';
import './ProjectToolsEditorModal.css';

const TOOL_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'search', label: 'Search' },
  { value: 'filter', label: 'Filter' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'http_request', label: 'HTTP request' },
  { value: 'webpage_scrape', label: 'Webpage scrape' },
  { value: 'entry_update', label: 'Entry update' },
  { value: 'entry_create', label: 'Entry create' },
  { value: 'tool_chain', label: 'Tool chain' },
  { value: 'scheduler', label: 'Scheduler' },
  { value: 'web_search', label: 'Web search' },
  { value: 'research', label: 'Research' },
  { value: 'mcp_call', label: 'MCP call' },
  { value: 'email_draft', label: 'Email draft' },
  { value: 'locked_record', label: 'Locked record' },
  { value: 'embedding', label: 'Embedding' },
];

function normalizeKind(tool) {
  return String(tool?.kind || tool?.tool_kind || 'search').toLowerCase();
}

function toolLabel(tool) {
  return tool?.name || tool?.slug || tool?.id || 'Tool';
}

/**
 * CRUD dialog for project tool allowlist.
 * Props:
 * - suffix: project horizon
 * - initialSelectedIds: string[] (empty = start from all catalog tools)
 * - seedAllWhenEmpty: when initialSelectedIds empty, select entire catalog on open
 * - onCancel()
 * - onApply(ids: string[])
 */
function ProjectToolsEditorModal({
  suffix = null,
  initialSelectedIds = [],
  seedAllWhenEmpty = true,
  horizonOnly = false,
  onCancel,
  onApply,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => [...(initialSelectedIds || [])]);
  const [scopeFilter, setScopeFilter] = useState(horizonOnly ? 'horizon' : 'horizon'); // horizon | global
  const [typeFilter, setTypeFilter] = useState('');
  const [textFilter, setTextFilter] = useState('');

  useEffect(() => {
    if (horizonOnly) setScopeFilter('horizon');
  }, [horizonOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const pageSize = 100;
        let page = 1;
        let items = [];
        let totalPages = 1;
        do {
          const res = await listVectorTools(page, pageSize, undefined, suffix || undefined);
          items = items.concat(Array.isArray(res.items) ? res.items : []);
          totalPages = Number(res.totalPages) || 1;
          page += 1;
        } while (page <= totalPages && page <= 50);
        const normalized = items.map((t) => ({
          id: String(t.id || '').trim(),
          name: t.name || t.slug || t.id,
          slug: t.slug || t.name || '',
          description: t.description || '',
          kind: normalizeKind(t),
          source: 'horizon',
        })).filter((t) => t.id);
        if (!cancelled) {
          setCatalog(normalized);
          const initial = Array.isArray(initialSelectedIds)
            ? initialSelectedIds.map((x) => String(x).trim()).filter(Boolean)
            : [];
          if (initial.length) {
            setSelectedIds(initial);
          } else if (seedAllWhenEmpty) {
            setSelectedIds(normalized.map((t) => t.id));
          } else {
            setSelectedIds([]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load tools');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suffix]);

  const fullCatalog = useMemo(() => {
    if (!horizonOnly && scopeFilter === 'global') {
      return mergeSyntheticToolsIntoPickerList(catalog).map((t) => ({
        ...t,
        kind: normalizeKind(t),
        source: t.source || (String(t.id || '').includes(':') ? 'global' : 'horizon'),
      }));
    }
    return catalog;
  }, [catalog, scopeFilter, horizonOnly]);

  const toolMap = useMemo(() => {
    const m = new Map();
    fullCatalog.forEach((t) => { if (t.id) m.set(t.id, t); });
    return m;
  }, [fullCatalog]);

  const filteredAvailable = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    return fullCatalog.filter((t) => {
      if (typeFilter && normalizeKind(t) !== typeFilter) return false;
      if (!q) return true;
      const hay = `${t.name || ''} ${t.slug || ''} ${t.description || ''} ${t.id || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [fullCatalog, textFilter, typeFilter]);

  const selectedTools = useMemo(() => {
    return selectedIds.map((id) => toolMap.get(id) || { id, name: id, slug: '', kind: 'unknown', missing: true });
  }, [selectedIds, toolMap]);

  const addTool = (id) => {
    const val = String(id || '').trim();
    if (!val) return;
    setSelectedIds((prev) => (prev.includes(val) ? prev : [...prev, val]));
  };

  const removeTool = (id) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const addAllFiltered = () => {
    const ids = filteredAvailable.map((t) => t.id).filter(Boolean);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const clearSelected = () => setSelectedIds([]);

  return (
    <div className="modal-backdrop project-tools-editor-backdrop">
      <div
        className="modal-panel project-tools-editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-tools-editor-title"
      >
        <div className="modal-header">
          <h3 id="project-tools-editor-title" style={{ margin: 0 }}>Edit project tools</h3>
          <button type="button" className="btn ghost" onClick={onCancel} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body project-tools-editor-body">
          {error && (
            <div className="error-banner" role="alert">
              <i className="fas fa-exclamation-triangle" /> {error}
            </div>
          )}

          <p className="subtle project-tools-editor-lead">
            Choose which tools this project&apos;s chat (and workflow buttons) may use.
            Clearing the right-hand list and clicking <strong>Use these tools</strong> saves an empty allowlist (no tools).
            Cancel returns to project settings without changing the draft.
            {suffix ? <> Horizon: <code>{suffix}</code>.</> : null}
          </p>

          <div className="project-tools-filters">
            <label className="project-tools-filter">
              Scope
              <select
                value={horizonOnly ? 'horizon' : scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                disabled={horizonOnly}
              >
                <option value="horizon">Current horizon</option>
                <option value="global" disabled={horizonOnly}>Global (horizon + platform)</option>
              </select>
            </label>
            <label className="project-tools-filter">
              Type
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                {TOOL_TYPE_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="project-tools-filter project-tools-filter-grow">
              Search
              <input
                type="search"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="Name, slug, or id…"
              />
            </label>
          </div>

          <div className="project-tools-columns">
            <section className="project-tools-column">
              <div className="project-tools-column-head">
                <h4>Available</h4>
                <button type="button" className="btn btn-sm ghost" onClick={addAllFiltered} disabled={loading || !filteredAvailable.length}>
                  Add filtered
                </button>
              </div>
              <div className="project-tools-list">
                {loading ? (
                  <div className="subtle"><i className="fas fa-spinner fa-pulse" /> Loading…</div>
                ) : filteredAvailable.length === 0 ? (
                  <div className="subtle">No tools match these filters.</div>
                ) : (
                  filteredAvailable.map((t) => {
                    const selected = selectedIds.includes(t.id);
                    return (
                      <div key={t.id} className={`project-tools-row ${selected ? 'is-selected' : ''}`}>
                        <div className="project-tools-row-main">
                          <strong>{toolLabel(t)}</strong>
                          <span className="project-tools-meta">
                            <code>{normalizeKind(t)}</code>
                            {t.slug ? <code>{t.slug}</code> : null}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm primary"
                          disabled={selected}
                          onClick={() => addTool(t.id)}
                        >
                          {selected ? 'Added' : 'Add'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="project-tools-column">
              <div className="project-tools-column-head">
                <h4>In this project ({selectedIds.length})</h4>
                <button type="button" className="btn btn-sm ghost" onClick={clearSelected} disabled={!selectedIds.length}>
                  Clear
                </button>
              </div>
              <div className="project-tools-list">
                {selectedTools.length === 0 ? (
                  <div className="subtle">No tools selected yet.</div>
                ) : (
                  selectedTools.map((t) => (
                    <div key={t.id} className={`project-tools-row ${t.missing ? 'is-missing' : ''}`}>
                      <div className="project-tools-row-main">
                        <strong>{toolLabel(t)}</strong>
                        <span className="project-tools-meta">
                          <code>{normalizeKind(t)}</code>
                          {t.missing ? <span className="project-tools-missing">missing on horizon</span> : null}
                        </span>
                      </div>
                      <button type="button" className="btn btn-sm ghost" onClick={() => removeTool(t.id)}>
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            onClick={() => onApply && onApply([...selectedIds])}
            disabled={loading}
          >
            Use these tools
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProjectToolsEditorModal;
